import { AxiosRequestConfig } from 'axios'

import { atlasClient } from 'api/atlasClient'
import { CerebroPaginatedResponse } from 'api/Cerebro/types'

export interface MarketAnalysisSegmentAttributes {
    name: string
    description: string | null
    query_definition: Record<string, unknown>
    shared_with_org: boolean
    user_id: number
    org_id: number
    app_id: string
    created_by: string
    created_at: string
    updated_at: string
}

export interface AtlasMarketAnalysisSegment {
    id: string
    type: string
    attributes: MarketAnalysisSegmentAttributes
}

export interface MarketAnalysisSegment {
    id: string
    name: string
    user_id: number
    description: string | null
    created_by: string
    created_at: string
    updated_at: string
    shared_with_org: boolean
    marketplace: string | null
    query_definition: Record<string, unknown>
}

interface AtlasSegmentListResponse {
    data: AtlasMarketAnalysisSegment[]
    meta: { total: number; limit: number; offset: number }
}

interface AtlasSegmentResourceResponse {
    data: AtlasMarketAnalysisSegment
}

export interface MarketAnalysisSegmentsListParams {
    limit?: number
    offset?: number
    search?: string
    user_id?: number
    ordering?: string
}

function extractMarketplace(qd: Record<string, unknown>): string | null {
    const value = qd?.marketplace
    return typeof value === 'string' && value.length > 0
        ? value.toLowerCase()
        : null
}

function flatten(segment: AtlasMarketAnalysisSegment): MarketAnalysisSegment {
    const { query_definition } = segment.attributes
    return {
        id: segment.id,
        name: segment.attributes.name,
        user_id: segment.attributes.user_id,
        description: segment.attributes.description,
        created_by: segment.attributes.created_by,
        created_at: segment.attributes.created_at,
        updated_at: segment.attributes.updated_at,
        shared_with_org: segment.attributes.shared_with_org,
        marketplace: extractMarketplace(query_definition),
        query_definition,
    }
}

export async function getMarketAnalysisSegments(
    params: MarketAnalysisSegmentsListParams = {},
    options: AxiosRequestConfig = {}
): Promise<CerebroPaginatedResponse<MarketAnalysisSegment>> {
    const response = await atlasClient.get<AtlasSegmentListResponse>(
        '/api/v1/segments',
        { ...options, params: { ...options.params, ...params } }
    )
    const { data: items, meta } = response.data
    return {
        ...response,
        data: {
            count: meta.total,
            next: null,
            previous: null,
            results: items.map(flatten),
        },
    }
}

export async function getMarketAnalysisSegment(
    id: string,
    options: AxiosRequestConfig = {}
): Promise<MarketAnalysisSegment> {
    const response = await atlasClient.get<AtlasSegmentResourceResponse>(
        `/api/v1/segments/${id}`,
        options
    )
    return flatten(response.data.data)
}
